import * as numbers from "common/numbers";

// State lives at input.program.renewalRateChange, keyed by block and year.
// Commissions are held here as percentages and converted to fractions in buildArgs.

export const DEFAULT_BLENDED_ILF = 1.86;

export const EXPOSURE_BASIS_OPTIONS = [
  "Revenue",
  "Payroll",
  "Total Cost",
  "Power Units",
  "Annual Mileage",
  "Other",
];
export const HAZARD_OPTIONS = ["Low", "Medium", "High"];
export const YES_NO_OPTIONS = ["Yes", "No"];
export const PORTFOLIO_OPTIONS = ["Include", "Exclude"];

export const PRIMARY_SECTIONS = {
  current: "primaryCurrent",
  expiring: "primaryExpiring",
};
export const EXCESS_SECTIONS = {
  current: "excessCurrent",
  expiring: "excessExpiring",
};

// `kind` drives the input rendered for the row; `format` drives the derived-row display.
export const PRIMARY_FIELDS = [
  {
    key: "exposureBasis",
    label: "Primary Exposure Basis",
    kind: "select",
    options: EXPOSURE_BASIS_OPTIONS,
  },
  { key: "exposureAmount", label: "Exposure Amount", kind: "number" },
  { key: "premium", label: "Our Share Premium", kind: "currency" },
  { key: "commissionPct", label: "Commission", kind: "percent" },
  { key: "deductible", label: "Deductible", kind: "currency" },
  { key: "occurrenceLimit", label: "Our Occurrence Limit", kind: "currency" },
];

export const EXCESS_FIELDS = [
  {
    key: "exposureBasis",
    label: "Primary GL Exposure Basis",
    kind: "select",
    options: EXPOSURE_BASIS_OPTIONS,
  },
  { key: "glExposureAmount", label: "GL Exposure Amount", kind: "number" },
  {
    key: "autoIncluded",
    label: "Auto Included",
    kind: "select",
    options: YES_NO_OPTIONS,
  },
  { key: "powerUnits", label: "Power Units", kind: "number" },
  {
    key: "glHazard",
    label: "GL Hazard",
    kind: "select",
    options: HAZARD_OPTIONS,
  },
  {
    key: "autoHazard",
    label: "Auto Hazard",
    kind: "select",
    options: HAZARD_OPTIONS,
  },
  { key: "xsPremiumGl", label: "Our Share XS Premium - GL", kind: "currency" },
  {
    key: "xsPremiumAuto",
    label: "Our Share XS Premium - Auto",
    kind: "currency",
  },
  { key: "commissionPct", label: "Commission", kind: "percent" },
  { key: "limit", label: "Our Share Limit", kind: "currency" },
  { key: "glAttachment", label: "GL Attachment", kind: "currency" },
  { key: "autoAttachment", label: "Auto Attachment", kind: "currency" },
];

// Current-year fields that are pricing INPUTS: each has a home on the submission, so editing
// one writes there instead of storing a local override and the two screens cannot drift.
// `toSource` converts the tab's units to the stored ones. An entry with `also` writes more homes
// too, the way the screen that owns the field does.
//
// Everything else on the tab is a pricing engine RESULT with nowhere to write back to:
// exposure amount, GL exposure amount, exposure basis and the blended ILF come from GL
// Pricing; both XS premiums and Auto Included come from XS Rating. Power units is an input but
// is the sum of nine vehicle counts, so a total cannot be written back to a single field.
// The premium is written back by `currentWriteBack`, as its home depends on GL Pricing.
export const CURRENT_WRITE_BACK = {
  // A cleared hazard is unset, so XS Rating falls back to its guideline hazard. XS Rating
  // cannot price an empty hazard.
  glHazard: {
    store: "xsRating",
    field: "premOpsHazard",
    toSource: (value) => (value === "" ? null : value),
  },
  autoHazard: {
    store: "xsRating",
    field: "autoHazard",
    toSource: (value) => (value === "" ? null : value),
  },
  // XS Rating shows the layer's limit first, so its Policy Limit field writes both.
  limit: {
    store: "layer",
    field: "limit",
    also: [{ store: "xsRating", field: "policyLimit" }],
  },
  // The GL XS premium prices premises/ops, products and liquor, so the GL attachment moves all
  // three. The tab reads it back from premises/ops.
  glAttachment: {
    store: "xsRating",
    field: "premOpsAttachment",
    also: [
      { store: "xsRating", field: "pcoAttachment" },
      { store: "xsRating", field: "liquorAttachment" },
    ],
  },
  autoAttachment: {
    store: "xsRating",
    field: "autoAttachment",
  },
  premium: { store: "layer", field: "shareOfPremium" },
  deductible: {
    store: "layer",
    field: "deductibleAmount",
  },
  occurrenceLimit: {
    store: "layer",
    field: "glOccurrenceLimit",
  },
  commissionPct: {
    store: "layer",
    field: "commissionPct",
    // The tab holds a percentage; the layer holds a decimal fraction.
    toSource: (value) => (value == null || value === "" ? null : value / 100),
  },
};

// The writes a current-year edit makes, as [{ store, field, value }], or null for a field
// that is kept as a local override. Once GL Pricing has run, the premium shown is its charged
// premium at our share, so a typed premium sets the manual premium adjustment instead, the way
// the Charged Premium field on GL Pricing does. Before that the premium is the layer's own.
export const currentWriteBack = (field, value, { resolved, layer } = {}) => {
  if (field === "premium") {
    const guideline = glTotals(resolved)?.guideline_premium;
    const shareOfLine = layer?.shareOfLine ?? 100;
    if (guideline != null) {
      if (!(shareOfLine > 0)) {
        // Our share of GL Pricing's premium is nothing, so no adjustment can produce this.
        return null;
      }
      const premium = numbers.parseNumberString(value);
      return [
        {
          store: "layer",
          field: "manualPremiumAdjustment",
          value:
            premium == null ? null : (premium * 100) / shareOfLine - guideline,
        },
      ];
    }
  }
  const target = CURRENT_WRITE_BACK[field];
  if (!target) {
    return null;
  }
  const sourceValue = target.toSource ? target.toSource(value) : value;
  return [target, ...(target.also ?? [])].map(
    ({ store, field: sourceField }) => ({
      store,
      field: sourceField,
      value: sourceValue,
    })
  );
};

const VEHICLE_KEYS = [
  "vehiclePpt",
  "vehicleLightTruck",
  "vehicleMediumTruck",
  "vehicleHeavyTruck",
  "vehicleExtraHeavyTruck",
  "vehicleHeavyTruckTractor",
  "vehicleExtraHeavyTruckTractor",
  "vehiclePassengerTransport",
  "vehicleOther",
];

export const totalPowerUnits = (autoExposureRating) => {
  const total = VEHICLE_KEYS.reduce(
    (sum, key) => sum + (Number(autoExposureRating?.[key]) || 0),
    0
  );
  return total > 0 ? total : null;
};

// GL Pricing writes its result to baseLayerBurn.pricing.values.totals.
const glTotals = (resolved) => resolved?.baseLayerBurn?.pricing?.values?.totals;

// GL Pricing charges the guideline premium plus the manual premium adjustment, at 100%. The
// adjustment is read off the layer, as GL Pricing prices with it, so an edit shows before GL
// Pricing is re-run. An empty adjustment prices as none.
const glChargedPremium = (resolved, layer) => {
  const totals = glTotals(resolved);
  if (totals?.guideline_premium == null) {
    return totals?.charged_premium ?? null;
  }
  const adjustment = layer
    ? numbers.parseNumberString(layer.manualPremiumAdjustment) ?? 0
    : totals.manual_premium_adjustment ?? 0;
  return totals.guideline_premium + adjustment;
};

// GL Pricing's charged premium scaled by our line, so it includes the manual premium
// adjustment. A submission GL Pricing has not priced falls back to the layer's share of premium.
const ourSharePremium = (resolved, layer) => {
  const charged = glChargedPremium(resolved, layer);
  if (charged == null) {
    return layer?.shareOfPremium != null
      ? roundDollars(layer.shareOfPremium)
      : null;
  }
  // No share entered means the whole line, as the XS engine reads `participation or 1.0`.
  const shareOfLine = layer?.shareOfLine ?? 100;
  return roundDollars((charged * shareOfLine) / 100);
};

// The sheet holds whole dollars.
const roundDollars = (value) =>
  value == null ? null : numbers.roundToNearestDollar(value);

// Only three GL class bases line up with the dropdown; the long tail reads as Other.
// Power Units and Annual Mileage are auto bases and never appear on a GL class.
const basisFromUnitLabel = (label) => {
  const text = String(label ?? "").toLowerCase();
  if (!text) return null;
  if (text.startsWith("gross sales")) return "Revenue";
  if (text.startsWith("payroll")) return "Payroll";
  if (
    text.startsWith("total cost") ||
    text.startsWith("total operating expenses")
  ) {
    return "Total Cost";
  }
  return "Other";
};

const glRecords = (resolved) =>
  glTotals(resolved) ? resolved?.baseLayerBurn?.pricing?.values?.records : null;

// GL Pricing returns the policy-level premises/ops ILF; re-run it if a submission predates
// the field.
export const blendedIlf = (resolved) => {
  const ilf = Number(glTotals(resolved)?.blended_prem_ops_ilf);
  if (!Number.isFinite(ilf) || ilf <= 0) {
    return null;
  }
  return Math.round(ilf * 10000) / 10000;
};

// A submission can mix bases, so the policy basis is whichever carries the most exposure.
export const dominantExposureBasis = (resolved) => {
  const records = glRecords(resolved);
  if (!Array.isArray(records) || records.length === 0) {
    return null;
  }
  const byBasis = new Map();
  records.forEach((record) => {
    const basis = basisFromUnitLabel(record?.basis_unit_label);
    if (!basis) return;
    const amount = Number(record?.exposure_amount) || 0;
    byBasis.set(basis, (byBasis.get(basis) ?? 0) + amount);
  });
  if (byBasis.size === 0) {
    return null;
  }
  return [...byBasis.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
  )[0][0];
};

// The XS engine already scales each subline by `participation`, so these are our share.
const xsGuideline = (xsRatingResult) => {
  const totals = xsRatingResult?.totals;
  if (!totals) {
    return { gl: null, auto: null };
  }
  const sum = (...values) => {
    const present = values.filter((value) => value != null);
    return present.length ? present.reduce((a, b) => a + b, 0) : null;
  };
  return {
    gl: sum(
      totals.fses_guideline_prem_ops,
      totals.fses_guideline_pco,
      totals.fses_guideline_liquor
    ),
    auto: totals.fses_guideline_auto ?? null,
  };
};

// XS Rating charges the FSES Guideline (rounded to $10) plus the manual premium adjustment.
// The charged premium is split between GL and Auto in the same mix as the guideline sublines,
// so GL plus Auto always adds up to it. The adjustment typed on XS Rating wins over the one
// the last Calculate used, as on the XS Rating screen.
export const xsChargedPremiums = (xsRating, xsRatingResult) => {
  const totals = xsRatingResult?.totals;
  const guideline = xsGuideline(xsRatingResult);
  if (guideline.gl == null && guideline.auto == null) {
    return { gl: null, auto: null };
  }
  const base = (guideline.gl ?? 0) + (guideline.auto ?? 0);
  const adjustment =
    numbers.parseNumberString(xsRating?.manualAdjustment) ??
    totals.manual_adjustment ??
    0;
  const charged = roundDollars((totals.fses_guideline ?? base) + adjustment);
  if (guideline.auto == null) {
    return { gl: charged, auto: null };
  }
  if (guideline.gl == null) {
    return { gl: null, auto: charged };
  }
  // With no guideline premium there is no mix to follow, so a flat adjustment goes on GL.
  const gl = base > 0 ? roundDollars((charged * guideline.gl) / base) : charged;
  return { gl, auto: charged - gl };
};

// XS Rating shows the layer's limit first and its own Policy Limit when the layer has none.
const xsPolicyLimit = (resolved, layer) =>
  layer?.limit ?? resolved?.xsRating?.policyLimit ?? null;

// The XS engine prices an attachment under $1M from the $1M band.
const XS_MIN_ATTACHMENT = 1000000;

// XS Rating shows its own attachment first and the layer's when it has none. A Calculate
// echoes the attachment it priced with. The first layer of a tower resolves to attachment 0
// when none was entered, so the value is lifted to the $1M the engine priced from.
const xsAttachment = (resolved, layer, xsRatingResult, { key, resultKey }) => {
  const value = Number(
    resolved?.xsRating?.[key] ??
      layer?.attachment ??
      xsRatingResult?.inputs?.[resultKey] ??
      NaN
  );
  return Number.isFinite(value) ? Math.max(value, XS_MIN_ATTACHMENT) : null;
};

// XS Rating shows its own hazard first and its guideline when none is set. The guideline the
// last Calculate used is echoed on the result. A blank from an earlier edit counts as unset.
const xsHazard = (resolved, xsRatingResult, { key, resultKey }) =>
  resolved?.xsRating?.[key] ||
  resolved?.xsRating?.[`${key}Guideline`] ||
  xsRatingResult?.inputs?.[resultKey] ||
  null;

// Each column is filled from that year's own submission. On a fresh renewal xsRatingResult
// carries forward, so the current-year XS figures show last year's until XS Rating is re-run.
export const suggestPrimary = (resolved, { activeLayer } = {}) => ({
  exposureBasis: dominantExposureBasis(resolved),
  exposureAmount: roundDollars(glTotals(resolved)?.total_exposure ?? null),
  premium: ourSharePremium(resolved, activeLayer),
  deductible: resolved?.glLimits?.deductible_amount ?? null,
  occurrenceLimit: resolved?.glLimits?.occurrence_limit ?? null,
  commissionPct:
    numbers.toPercentFromDecimal(resolved?.glLimits?.commission_pct) ?? null,
});

export const suggestExcess = (
  resolved,
  { xsRatingResult, activeLayer } = {}
) => {
  const guideline = xsGuideline(xsRatingResult);
  const charged = xsChargedPremiums(resolved?.xsRating, xsRatingResult);
  return {
    exposureBasis: dominantExposureBasis(resolved),
    glExposureAmount: roundDollars(glTotals(resolved)?.total_exposure ?? null),
    autoIncluded:
      guideline.auto == null ? null : guideline.auto > 0 ? "Yes" : "No",
    powerUnits: totalPowerUnits(resolved?.autoExposureRating),
    glHazard: xsHazard(resolved, xsRatingResult, {
      key: "premOpsHazard",
      resultKey: "prem_ops_hazard_guideline",
    }),
    autoHazard: xsHazard(resolved, xsRatingResult, {
      key: "autoHazard",
      resultKey: "auto_hazard_guideline",
    }),
    xsPremiumGl: charged.gl,
    xsPremiumAuto: charged.auto,
    limit: xsPolicyLimit(resolved, activeLayer),
    glAttachment: xsAttachment(resolved, activeLayer, xsRatingResult, {
      key: "premOpsAttachment",
      resultKey: "prem_ops_attachment",
    }),
    autoAttachment: xsAttachment(resolved, activeLayer, xsRatingResult, {
      key: "autoAttachment",
      resultKey: "auto_attachment",
    }),
    commissionPct:
      numbers.toPercentFromDecimal(resolved?.glLimits?.commission_pct) ?? null,
  };
};

// A stored value always wins, including 0 or "", so clearing a prefilled cell sticks.
export const resolveValues = (stored, suggested) => {
  const merged = { ...suggested };
  Object.entries(stored ?? {}).forEach(([key, value]) => {
    if (value !== undefined) {
      merged[key] = value;
    }
  });
  return merged;
};

export const buildArgs = ({ blendedIlf, primary, excess }) => ({
  blended_ilf:
    blendedIlf == null || blendedIlf === ""
      ? DEFAULT_BLENDED_ILF
      : Number(blendedIlf),
  primary: {
    current: primaryYear(primary?.current),
    expiring: primaryYear(primary?.expiring),
  },
  excess: {
    current: excessYear(excess?.current),
    expiring: excessYear(excess?.expiring),
  },
});

const primaryYear = (year) => ({
  exposure_basis: year?.exposureBasis ?? null,
  exposure_amount: numbers.parseNumberString(year?.exposureAmount) ?? 0,
  premium: numbers.parseNumberString(year?.premium) ?? 0,
  commission: numbers.toDecimalFromPercent(year?.commissionPct) ?? 0,
  deductible: numbers.parseNumberString(year?.deductible) ?? 0,
  occurrence_limit: numbers.parseNumberString(year?.occurrenceLimit) ?? 0,
});

const excessYear = (year) => ({
  exposure_basis: year?.exposureBasis ?? null,
  gl_exposure_amount: numbers.parseNumberString(year?.glExposureAmount) ?? 0,
  auto_included: year?.autoIncluded ?? null,
  power_units: numbers.parseNumberString(year?.powerUnits) ?? 0,
  gl_hazard: year?.glHazard ?? null,
  auto_hazard: year?.autoHazard ?? null,
  xs_premium_gl: numbers.parseNumberString(year?.xsPremiumGl) ?? 0,
  xs_premium_auto: numbers.parseNumberString(year?.xsPremiumAuto) ?? 0,
  commission: numbers.toDecimalFromPercent(year?.commissionPct) ?? 0,
  limit: numbers.parseNumberString(year?.limit) ?? 0,
  gl_attachment: numbers.parseNumberString(year?.glAttachment) ?? 0,
  auto_attachment: numbers.parseNumberString(year?.autoAttachment) ?? 0,
});

// How a locked input renders: same value, no field around it.
export const formatByKind = (value, kind) => {
  if (value == null || value === "") {
    return "\u2014";
  }
  if (kind === "currency") {
    return formatCurrency(Number(value));
  }
  if (kind === "percent") {
    return `${Number(value)}%`;
  }
  if (kind === "number") {
    return numbers.dpString(0)(Number(value));
  }
  return String(value);
};

// The shared formatters return N/A or null for a non-number; the sheet shows a dash.
export const formatCurrency = (value) =>
  numbers.currencyFormat(value, 0) ?? "\u2014";

export const formatPercent = (value) =>
  value == null ? "\u2014" : numbers.percentage(2)(value);

export const formatFactor = (value) =>
  value == null ? "\u2014" : numbers.dpString(4)(value);
